import { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// Import validation
import { defaultValues, schema, type InvoiceFormValues } from 'helpers/validations/invoice';

// Import api
import { useUpdateInvoiceInfoMutation } from 'store/features/invoice';

// Import helpers
import { checkValidatorErrors } from 'helpers/validator_errors';

// Import types
import type { InvoiceInfoType } from 'types/api/invoice';
import type { RTKQueryError } from 'types/api/errors';

type Params = {
	invoice: InvoiceInfoType | null;
	closeModal: () => void;
};

export const useInvoiceModal = ({ invoice, closeModal }: Params) => {
	const [update, { isLoading }] = useUpdateInvoiceInfoMutation();

	const methods = useForm<InvoiceFormValues>({
		resolver: zodResolver(schema),
		disabled: isLoading,
		defaultValues: {
			need_invoice: true,
			client: invoice ?? defaultValues
		}
	});

	useEffect(() => {
		const subscription = methods.watch((values, { name, type }) => {
			if (!type || name !== 'client.type') return;
			methods.clearErrors();
		});

		return () => subscription.unsubscribe();
	}, []);

	const handleInvoiceSubmit: SubmitHandler<InvoiceFormValues> = (values) => {
		update(values)
			.unwrap()
			.then(closeModal)
			.catch((response: RTKQueryError) => {
				checkValidatorErrors({ response }).then((errors) => {
					if (!errors) return;
					Object.entries(errors).forEach(([field, message]) => {
						const fieldName = field as keyof InvoiceFormValues;
						methods.setError(fieldName, { type: 'custom', message });
					});
				});
			});
	};

	return { methods, isLoading, handleInvoiceSubmit };
};
